'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';
import '@/styles/brutalist/base.css';
import { LoginModal } from './login-modal';

interface AuthUser {
  id: string;
  email: string;
  companyName: string | null;
  role: string | null;
  hasStripeCustomer: boolean;
}

export function BrutalistHeader() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const supabase = useMemo(() => createClient(), []);

  const fetchAuthState = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        return data.user as AuthUser | null;
      }
    } catch (error) {
      console.error('[Header] Auth check failed:', error);
    }
    return null;
  }, []);

  useEffect(() => {
    let mounted = true;

    fetchAuthState().then((authUser) => {
      if (!mounted) return;
      setUser(authUser);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        const authUser = await fetchAuthState();
        if (mounted) {
          setUser(authUser);
          setLoading(false);
        }
      }
      // Only clear the user on SIGNED_OUT so that transient network errors or
      // token-refresh races don't wipe a valid session from the header UI.
      if (event === 'SIGNED_OUT') {
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, fetchAuthState]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await supabase.auth.signOut();
    setUser(null);
    window.location.href = '/';
  };

  const handleManageBilling = async () => {
    setDropdownOpen(false);
    try {
      const response = await fetch('/api/customer-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.url) window.location.href = data.url;
      }
    } catch (err) {
      console.error('Customer portal error:', err);
    }
  };

  return (
    <>
      <header className="brutalist-header">
        <nav className="nav-grid">
          <div className="brand">
            <Link href="/">
              <Image src="/logo.svg" alt="Shipzy" width={120} height={36} className="brand-logo" />
            </Link>
          </div>
          <div className="nav-links">
            <Link href="/for-forwarders" className="nav-link nav-link--primary">For Forwarders</Link>
            <Link href="/directory" className="nav-link">Directory</Link>
            <Link href="/schedules" className="nav-link">Schedules</Link>
            <Link href="/track" className="nav-link">Track</Link>
            <Link href="/blog" className="nav-link">Blog</Link>
            <Link href="/pricing" className="nav-link">Pricing</Link>
          </div>

          {loading ? (
            <div className="user-actions" style={{ visibility: 'hidden' }}>
              Login / Register
            </div>
          ) : user ? (
            <div className="user-actions user-actions--logged-in" ref={dropdownRef}>
              <button
                className="user-company-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className="user-company-name">{user.companyName || user.email?.split('@')[0] || 'My Account'}</span>
                <svg
                  className={`user-chevron ${dropdownOpen ? 'user-chevron--open' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  <Link
                    href={user.role === 'shipper' ? '/dashboard/my-shipments' : '/forwarders/dashboard'}
                    className="user-dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {user.hasStripeCustomer && user.role === 'shipper' && (
                    <button
                      className="user-dropdown-item"
                      onClick={handleManageBilling}
                    >
                      Manage Billing
                    </button>
                  )}
                  <div className="user-dropdown-separator" />
                  <button
                    className="user-dropdown-item user-dropdown-item--danger"
                    onClick={handleLogout}
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="user-actions" onClick={() => setLoginModalOpen(true)}>
              Login / Register
            </button>
          )}
        </nav>
      </header>
      <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
    </>
  );
}
